import type { Answers } from './types';
import { emptyAnswers } from './types';

export interface Fixture {
  id: string;
  label: string;
  expectedPrimary: string;
  /** Fictional artist, written to pressure-test the result. */
  story: string;
  answers: Answers;
}

const make = (partial: Partial<Answers>): Answers => ({ ...emptyAnswers(), ...partial });

export const fixtures: Fixture[] = [
  {
    id: 'content-old-address',
    label: 'Content: released guitar track, announcement-led promotion',
    expectedPrimary: 'C01',
    story:
      'Mara Quell, 27, writes guitar-led songs recorded in her flat. Released "Old Address" six weeks ago. Everything she has posted since is the artwork with the release date, then a reminder of the release date. She gets 300 to 600 views, a handful of likes from people she knows, and nothing anyone has said about the song itself. She hates being on camera but will film her hands. About an hour a week around shift work.',
    answers: make({
      goal: ['MORE_DISCOVERY'],
      readiness: ['RELEASED'],
      songTitle: 'Old Address',
      audience: ['SPECIFIC_REASON'],
      discovery: ['OWN_CONTENT'],
      introduction: ['ANNOUNCEMENTS'],
      response: ['LIGHT_ATTENTION'],
      nextStep: ['CLEAR_DESTINATION'],
      return: ['NOT_YET'],
      learning: ['REPEAT_FEELING'],
      capacity: ['SMALL'],
      personalisation: {
        first_moment: 'I still know your postcode, but not how you are',
        format_comfort: 'HANDS',
        recent_caption: 'Out now on all platforms, link in bio, means the world if you stream it',
      },
    }),
  },
  {
    id: 'audience-everyone',
    label: 'Audience: song-led posts, no listener chosen',
    expectedPrimary: 'A01',
    story:
      'Deniz Ari, 22, makes slow electronic tracks with a live bass underneath. Released "Half Signal" last month and has been posting short clips with the music up front, which is the right instinct. Asked who it is for, the honest answer is anyone who likes it. Views sit around 1,200 with likes and no comments about the music. Two to three hours a week, comfortable on camera but prefers not to talk.',
    answers: make({
      goal: ['MORE_DISCOVERY'],
      readiness: ['RELEASED'],
      songTitle: 'Half Signal',
      audience: ['EVERYONE'],
      discovery: ['OWN_CONTENT'],
      introduction: ['SONG_LED'],
      response: ['LIGHT_ATTENTION'],
      nextStep: ['CLEAR_DESTINATION'],
      return: ['NOT_YET'],
      learning: ['COMPARE'],
      capacity: ['MEDIUM'],
      personalisation: {
        sound_description:
          'Slow electronic, warm analogue synths, a live fretless bass and a half-whispered vocal. Closer to late-night driving music than dance music.',
        reference_detail: '',
        keep_making: 'The bass playing. It is the part that makes it mine rather than a preset.',
      },
    }),
  },
  {
    id: 'discovery-existing-circle',
    label: 'Discovery: everything comes from people who already know her',
    expectedPrimary: 'D01',
    story:
      'Joelle Prentice, 31, country-folk, released "Fenceline" in the spring. She posts clips with the music leading, and the response is warm, but it is the same forty people every time: former bandmates, family, the choir she sings with. She has never approached anyone she does not know. Judges posts on views. Around an hour a week.',
    answers: make({
      goal: ['MORE_DISCOVERY'],
      readiness: ['RELEASED'],
      songTitle: 'Fenceline',
      audience: ['SPECIFIC_REASON'],
      discovery: ['EXISTING_CIRCLE'],
      introduction: ['SONG_LED'],
      response: ['LIGHT_ATTENTION'],
      nextStep: ['CLEAR_DESTINATION'],
      return: ['NOT_YET'],
      learning: ['VIEWS_ONLY'],
      capacity: ['SMALL'],
      personalisation: {
        music_description: 'Country-folk, acoustic guitar and close harmony, written about small-town leaving',
        route_choice: 'CURATOR',
        existing_access: 'None yet. I know a local radio show exists that plays new country but I have never contacted them.',
      },
    }),
  },
  {
    id: 'next-step-no-route',
    label: 'Next Step: broad audience and no working route to the song',
    expectedPrimary: 'N01',
    story:
      'Tobi Adeyemi, 24, makes rap over sampled soul. Released "Coach Money" three weeks ago. People message asking where to hear it, and his bio currently points at an old page with three older tracks and no mention of the new one. He does not know who the song is for beyond anyone who likes it. Three hours a week, happy on camera.',
    answers: make({
      goal: ['FIRST_LISTENERS'],
      readiness: ['RELEASED'],
      songTitle: 'Coach Money',
      audience: ['EVERYONE'],
      discovery: ['OWN_CONTENT'],
      introduction: ['SONG_LED', 'SONG_CONTEXT'],
      response: ['LIGHT_ATTENTION'],
      nextStep: ['NO_ROUTE'],
      return: ['NOT_YET'],
      learning: ['COMPARE'],
      capacity: ['MEDIUM'],
      personalisation: {
        current_cta: 'new one out, go check it',
        destination_report:
          'My bio link goes to an old page with three songs from two years ago. Coach Money is not on it anywhere. You would have to search my name again to find it.',
        available_destination: 'RELEASED_SONG',
      },
    }),
  },
  {
    id: 'return-no-follow-up',
    label: 'Return: real interest after a show, then nothing',
    expectedPrimary: 'R01',
    story:
      'The Welt, a four-piece from Sheffield. Played six shows this spring and released "Dry Season". After each show a handful of people find them and say they went home and listened, and two asked when the next one is out. Then the band goes quiet for a month and starts writing again. They have an unreleased live recording from the same set sitting on a hard drive.',
    answers: make({
      goal: ['MORE_RETURN'],
      readiness: ['RELEASED'],
      songTitle: 'Dry Season',
      audience: ['SPECIFIC_REASON'],
      discovery: ['LIVE'],
      introduction: ['SONG_LED'],
      response: ['SONG_ENQUIRIES', 'DELIBERATE_INTEREST'],
      nextStep: ['CLEAR_DESTINATION'],
      return: ['MOVE_ON'],
      learning: ['COMPARE'],
      capacity: ['MEDIUM'],
      personalisation: {
        responded_song:
          'Dry Season. Two people at the Leadmill said they listened on the train home, and one asked whether the live version exists anywhere.',
        available_next: 'DIFFERENT_PERFORMANCE',
        format_preference: 'PERFORMANCE',
      },
    }),
  },
  {
    id: 'testing-change-everything',
    label: 'Testing: real attempts, everything changes between them',
    expectedPrimary: 'T01',
    story:
      'Ren Machado, 29, instrumental guitar. Released "Cold Open" and posts two or three times a week, always with the music leading. Every post is a different song, different length, different caption style, filmed somewhere new. Some do 400 views, one did 9,000, and he cannot work out why. He follows up properly and has a clear link. Three hours a week.',
    answers: make({
      goal: ['MORE_DISCOVERY'],
      readiness: ['RELEASED'],
      songTitle: 'Cold Open',
      audience: ['SPECIFIC_REASON'],
      discovery: ['OWN_CONTENT'],
      introduction: ['SONG_LED'],
      response: ['LIGHT_ATTENTION'],
      nextStep: ['CLEAR_DESTINATION'],
      return: ['RELATED_FOLLOW_UP'],
      learning: ['CHANGE_EVERYTHING'],
      capacity: ['MEDIUM'],
      personalisation: {
        activity_under_review: 'Short clips of the opening riff of Cold Open, posted two or three times a week',
        available_observations: 'POST_DATA',
        attempt_pair:
          'One was filmed outside in daylight with the riff starting straight away, one was indoors with four seconds of me tuning first. The outdoor one did twenty times the views but I also posted it on a Saturday.',
      },
    }),
  },
  {
    id: 'baseline-no-shareable',
    label: 'Starting from zero: no shareable song or excerpt yet',
    expectedPrimary: 'B02',
    story:
      'Sam Idowu, 19, has been writing for two years and has eleven songs in voice memos, none finished, none recorded properly. Wants to start promoting but has nothing anyone could listen to. Answers mostly not sure. Less than thirty minutes a week around college.',
    answers: make({
      goal: ['FIRST_LISTENERS'],
      readiness: ['NO_SHAREABLE'],
      songTitle: '',
      audience: ['UNSURE'],
      discovery: ['NOT_STARTED'],
      introduction: ['VERY_LITTLE'],
      response: ['NO_RECENT_ATTEMPT'],
      nextStep: ['NO_ROUTE'],
      return: ['NOT_YET'],
      learning: ['TOO_EARLY'],
      capacity: ['TINY'],
      personalisation: {
        shareable_moment: '',
        sharing_method: 'PHONE_VIDEO',
        blocker: 'None of them feel finished enough and I keep starting new ones instead.',
      },
    }),
  },
  {
    id: 'no-clear-gap',
    label: 'No clear gap: coherent approach, nothing obviously broken',
    expectedPrimary: 'NO_CLEAR_GAP',
    story:
      'Hana Brecht, 35, plays regularly around Bristol and released "Quiet Trade" in January. She knows who the song is for and can say why. People come back to shows and message about specific songs. She always follows a release with a second piece of related material, and she compares posts against each other before repeating anything. She is not sure what to change next, which is why she took this.',
    answers: make({
      goal: ['MORE_RETURN'],
      readiness: ['RELEASED'],
      songTitle: 'Quiet Trade',
      audience: ['SPECIFIC_REASON'],
      discovery: ['LIVE'],
      introduction: ['SONG_LED', 'SONG_CONTEXT'],
      response: ['DELIBERATE_INTEREST', 'RETURNING_INTEREST'],
      nextStep: ['CLEAR_DESTINATION'],
      return: ['RELATED_FOLLOW_UP'],
      learning: ['COMPARE'],
      capacity: ['MEDIUM'],
      personalisation: {
        activity_under_review: 'The live clip of Quiet Trade from the Louisiana in February',
        available_observations: 'REPLIES',
        attempt_pair: '',
      },
    }),
  },
];
